/* eslint-disable max-lines --  this is essentially a config file, so we're fine with disabling the line limit */
import { Modifier } from '../../../cross-team-shared/misc-services/events/event-helper';
import { KeyboardShortcutName } from '../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { InteractionMode } from '../../../team-apis/core/interaction-modes';

type KeyboardEventType = 'keydown' | 'keyup';

export class KeyboardShortcut {
  allowRepeat: boolean;
  modifiers: Modifier[];
  type: KeyboardEventType;
  allowedInteractionModes: InteractionMode[];
  mustHaveFeatureFlag: string;
  mustNotHaveFeatureFlag: string;

  constructor(
    public key: string,
    {
      allowRepeat = false,
      modifiers = [] as Modifier[],
      type = 'keydown' as KeyboardEventType,
      allowedInteractionModes = ['all'] as InteractionMode[],
      mustHaveFeatureFlag = '',
      mustNotHaveFeatureFlag = '',
    } = {},
  ) {
    this.allowRepeat = allowRepeat;
    this.modifiers = modifiers;
    this.type = type;
    this.allowedInteractionModes = allowedInteractionModes;
    this.mustHaveFeatureFlag = mustHaveFeatureFlag;
    this.mustNotHaveFeatureFlag = mustNotHaveFeatureFlag;
  }
}

export const keyboardShortcuts = {
  // If you add something here, think about adding it to the help modals too
  altDown: [new KeyboardShortcut('Alt', { modifiers: ['altKey'] })],
  altUp: [new KeyboardShortcut('Alt', { type: 'keyup' })],
  cancelTool: [
    new KeyboardShortcut('Escape', {
      allowedInteractionModes: ['canvas', 'none', 'all'],
    }),
  ],
  changeShapeVisibility: [new KeyboardShortcut('y')],
  clearSelection: [new KeyboardShortcut('Escape')],
  contractDown: [
    new KeyboardShortcut('s', {
      allowRepeat: true,
      modifiers: ['altKey'],
    }),
    // Fallback for non-chrome users
    new KeyboardShortcut('ß', {
      allowRepeat: true,
      modifiers: ['altKey'],
    }),
  ],
  contractDownSlow: [
    new KeyboardShortcut('s', {
      allowRepeat: true,
      modifiers: ['altKey', 'shiftKey'],
    }),
    // Fallback for non-chrome users
    new KeyboardShortcut('Í', {
      allowRepeat: true,
      modifiers: ['altKey', 'shiftKey'],
    }),
  ],
  contractLeft: [
    new KeyboardShortcut('a', {
      allowRepeat: true,
      modifiers: ['altKey'],
    }),
    // Fallback for non-chrome users
    new KeyboardShortcut('å', {
      allowRepeat: true,
      modifiers: ['altKey'],
    }),
  ],
  contractLeftSlow: [
    new KeyboardShortcut('a', {
      allowRepeat: true,
      modifiers: ['altKey', 'shiftKey'],
    }),
    // Fallback for non-chrome users
    new KeyboardShortcut('å', {
      allowRepeat: true,
      modifiers: ['altKey', 'shiftKey'],
    }),
  ],
  contractHeight: [
    new KeyboardShortcut('q', {
      allowRepeat: true,
      modifiers: ['altKey'],
    }),
    new KeyboardShortcut('œ', {
      allowRepeat: true,
      modifiers: ['altKey'],
    }),
  ],
  contractHeightSlow: [
    new KeyboardShortcut('q', {
      allowRepeat: true,
      modifiers: ['altKey', 'shiftKey'],
    }),
    new KeyboardShortcut('œ', {
      allowRepeat: true,
      modifiers: ['altKey', 'shiftKey'],
    }),
  ],
  copySelectedShapes: [new KeyboardShortcut('c', { modifiers: ['platform'] })],
  copySelectedShapesAllFrames: [
    new KeyboardShortcut('c', { modifiers: ['platform', 'shiftKey'] }),
  ],
  copyLastKeyframe: [new KeyboardShortcut('k', { modifiers: ['shiftKey'] })],
  decreaseBrightness: [
    new KeyboardShortcut('-', {
      allowRepeat: true,
      allowedInteractionModes: ['all', 'canvas'],
    }),
  ],
  decreaseContrast: [
    new KeyboardShortcut('_', {
      allowRepeat: true,
      modifiers: ['shiftKey'],
      allowedInteractionModes: ['all', 'canvas'],
    }),
  ],
  decreaseOpacity: [
    new KeyboardShortcut('[', {
      allowRepeat: true,
      allowedInteractionModes: ['all', 'canvas'],
    }),
  ],
  deleteShapes: [
    new KeyboardShortcut('Delete'),
    new KeyboardShortcut('Backspace'),
  ],
  expandRight: [
    new KeyboardShortcut('d', {
      allowRepeat: true,
      modifiers: ['altKey'],
    }),
    // Fallback for non-chrome users
    new KeyboardShortcut('∂', {
      allowRepeat: true,
      modifiers: ['altKey'],
    }),
  ],
  expandRightSlow: [
    new KeyboardShortcut('d', {
      allowRepeat: true,
      modifiers: ['altKey', 'shiftKey'],
    }),
    // Fallback for non-chrome users
    new KeyboardShortcut('Î', {
      allowRepeat: true,
      modifiers: ['altKey', 'shiftKey'],
    }),
  ],
  expandUp: [
    new KeyboardShortcut('w', {
      allowRepeat: true,
      modifiers: ['altKey'],
    }),
    // Fallback for non-chrome users
    new KeyboardShortcut('∑', {
      allowRepeat: true,
      modifiers: ['altKey'],
    }),
  ],
  expandUpSlow: [
    new KeyboardShortcut('w', {
      allowRepeat: true,
      modifiers: ['altKey', 'shiftKey'],
    }),
    // Fallback for non-chrome users
    new KeyboardShortcut('„', {
      allowRepeat: true,
      modifiers: ['altKey', 'shiftKey'],
    }),
  ],
  expandHeight: [
    new KeyboardShortcut('e', {
      allowRepeat: true,
      modifiers: ['altKey'],
    }),
    new KeyboardShortcut('Dead', {
      allowRepeat: true,
      modifiers: ['altKey'],
    }),
  ],
  expandHeightSlow: [
    new KeyboardShortcut('e', {
      allowRepeat: true,
      modifiers: ['altKey', 'shiftKey'],
    }),
    new KeyboardShortcut('´', {
      allowRepeat: true,
      modifiers: ['altKey', 'shiftKey'],
    }),
  ],
  increaseBrightness: [
    new KeyboardShortcut('=', {
      allowRepeat: true,
      allowedInteractionModes: ['all', 'canvas'],
    }),
  ],
  increaseContrast: [
    new KeyboardShortcut('+', {
      allowRepeat: true,
      modifiers: ['shiftKey'],
      allowedInteractionModes: ['all', 'canvas'],
    }),
  ],
  increaseOpacity: [
    new KeyboardShortcut(']', {
      allowRepeat: true,
      allowedInteractionModes: ['all', 'canvas'],
    }),
  ],
  moveSliderLeft: [new KeyboardShortcut('q', { allowRepeat: true })],
  moveSliderLeftSlow: [
    new KeyboardShortcut('q', {
      allowRepeat: true,
      modifiers: ['shiftKey'],
    }),
  ],
  moveSliderRight: [new KeyboardShortcut('e', { allowRepeat: true })],
  moveSliderRightSlow: [
    new KeyboardShortcut('e', {
      allowRepeat: true,
      modifiers: ['shiftKey'],
    }),
  ],
  nudgeDown: [
    new KeyboardShortcut('s', {
      allowRepeat: true,
    }),
  ],
  nudgeDownSlow: [
    new KeyboardShortcut('s', {
      modifiers: ['shiftKey'],
      allowRepeat: true,
    }),
  ],
  nudgeInwards: [
    new KeyboardShortcut('q', {
      allowRepeat: true,
    }),
  ],
  nudgeInwardsSlow: [
    new KeyboardShortcut('q', {
      modifiers: ['shiftKey'],
      allowRepeat: true,
    }),
  ],
  nudgeLeft: [
    new KeyboardShortcut('a', {
      allowRepeat: true,
    }),
  ],
  nudgeLeftSlow: [
    new KeyboardShortcut('a', {
      modifiers: ['shiftKey'],
      allowRepeat: true,
    }),
  ],
  nudgeOutwards: [
    new KeyboardShortcut('e', {
      allowRepeat: true,
    }),
  ],
  nudgeOutwardsSlow: [
    new KeyboardShortcut('e', {
      modifiers: ['shiftKey'],
      allowRepeat: true,
    }),
  ],
  nudgeRight: [
    new KeyboardShortcut('d', {
      allowRepeat: true,
    }),
  ],
  nudgeRightSlow: [
    new KeyboardShortcut('d', {
      modifiers: ['shiftKey'],
      allowRepeat: true,
    }),
  ],
  nudgeUp: [
    new KeyboardShortcut('w', {
      allowRepeat: true,
    }),
  ],
  nudgeUpSlow: [
    new KeyboardShortcut('w', {
      modifiers: ['shiftKey'],
      allowRepeat: true,
    }),
  ],
  panDown: [
    new KeyboardShortcut('s', {
      allowRepeat: true,
      allowedInteractionModes: ['all', 'canvas'],
    }),
  ],
  panLeft: [
    new KeyboardShortcut('a', {
      allowRepeat: true,
      allowedInteractionModes: ['all', 'canvas'],
    }),
  ],
  panRight: [
    new KeyboardShortcut('d', {
      allowRepeat: true,
      allowedInteractionModes: ['all', 'canvas'],
    }),
  ],
  panUp: [
    new KeyboardShortcut('w', {
      allowRepeat: true,
      allowedInteractionModes: ['all', 'canvas'],
    }),
  ],
  pasteShapes: [new KeyboardShortcut('v', { modifiers: ['platform'] })],
  pitchLeft: [
    new KeyboardShortcut('ArrowUp', {
      allowRepeat: true,
    }),
  ],
  pitchLeftSlow: [
    new KeyboardShortcut('ArrowUp', {
      modifiers: ['shiftKey'],
      allowRepeat: true,
    }),
  ],
  pitchRight: [
    new KeyboardShortcut('ArrowDown', {
      allowRepeat: true,
    }),
  ],
  pitchRightSlow: [
    new KeyboardShortcut('ArrowDown', {
      modifiers: ['shiftKey'],
      allowRepeat: true,
    }),
  ],
  playPause: [new KeyboardShortcut(' ')],
  redo: [new KeyboardShortcut('z', { modifiers: ['platform', 'shiftKey'] })],
  redraw: [new KeyboardShortcut('m', { modifiers: ['platform'] })],
  resetSensorView: [new KeyboardShortcut('0')],
  rollLeft: [
    new KeyboardShortcut('ArrowLeft', {
      modifiers: ['altKey'],
      allowRepeat: true,
    }),
  ],
  rollLeftSlow: [
    new KeyboardShortcut('ArrowLeft', {
      modifiers: ['altKey', 'shiftKey'],
      allowRepeat: true,
    }),
  ],
  rollRight: [
    new KeyboardShortcut('ArrowRight', {
      modifiers: ['altKey'],
      allowRepeat: true,
    }),
  ],
  rollRightSlow: [
    new KeyboardShortcut('ArrowRight', {
      modifiers: ['altKey', 'shiftKey'],
      allowRepeat: true,
    }),
  ],
  scopeObject: [new KeyboardShortcut('Enter')],
  scrubBackward: [
    new KeyboardShortcut(',', {
      allowRepeat: true,
      allowedInteractionModes: ['scrubber', 'all'],
    }),
  ],
  scrubForward: [
    new KeyboardShortcut('.', {
      allowRepeat: true,
      allowedInteractionModes: ['scrubber', 'all'],
    }),
  ],
  selectAllShapes: [new KeyboardShortcut('a', { modifiers: ['platform'] })],
  selectArrowTool: [new KeyboardShortcut('u')],
  selectCuboidTool: [new KeyboardShortcut('c')],
  selectEditTool: [new KeyboardShortcut('v')],
  selectFillTool: [new KeyboardShortcut('f')],
  selectKeypointTool: [new KeyboardShortcut('k')],
  selectLineTool: [new KeyboardShortcut('l')],
  selectMeasureTool: [new KeyboardShortcut('m')],
  selectPaintbrushTool: [new KeyboardShortcut('b')],
  selectPointTool: [new KeyboardShortcut('x')],
  selectPolygonTool: [new KeyboardShortcut('p')],
  selectRectangleTool: [new KeyboardShortcut('g')],
  selectSelectTool: [new KeyboardShortcut('v')],
  selectNextAnnotation: [new KeyboardShortcut('tab')],
  selectPreviousAnnotation: [
    new KeyboardShortcut('tab', { modifiers: ['shiftKey'] }),
  ],
  selectSliderRectangleTool: [new KeyboardShortcut('t')],
  selectSuperpixelTool: [new KeyboardShortcut('u')],
  selectZoomTool: [new KeyboardShortcut('z')],
  shiftDown: [new KeyboardShortcut('Shift', { modifiers: ['shiftKey'] })],
  shiftUp: [new KeyboardShortcut('Shift', { type: 'keyup' })],
  showHints: [new KeyboardShortcut('/', { modifiers: ['platform'] })],
  skipScrubBackward: [
    new KeyboardShortcut('<', {
      modifiers: ['shiftKey'],
      allowRepeat: true,
      allowedInteractionModes: ['scrubber', 'all'],
    }),
  ],
  skipScrubForward: [
    new KeyboardShortcut('>', {
      modifiers: ['shiftKey'],
      allowRepeat: true,
      allowedInteractionModes: ['scrubber', 'all'],
    }),
  ],
  smartSkipBackward: [
    new KeyboardShortcut('l', {
      allowRepeat: true,
      allowedInteractionModes: ['scrubber', 'all'],
    }),
  ],
  smartSkipForward: [
    new KeyboardShortcut(';', {
      allowRepeat: true,
      allowedInteractionModes: ['scrubber', 'all'],
    }),
  ],
  cycleSelectedOutputInSplitView: [new KeyboardShortcut('`')],
  toggle3DShortcuts: [new KeyboardShortcut('`')],
  toggleEnhancedPointCloud: [new KeyboardShortcut('h')],
  toggleInspectMode: [new KeyboardShortcut('i')],
  toggleKeyframe: [new KeyboardShortcut('k')],
  toggleOrthographicCamera: [new KeyboardShortcut('o')],
  toggleZoomPanel: [
    new KeyboardShortcut('z', {
      modifiers: ['shiftKey'],
      allowedInteractionModes: ['all', 'canvas'],
    }),
  ],
  undo: [new KeyboardShortcut('z', { modifiers: ['platform'] })],
  undoSubpoint: [
    new KeyboardShortcut('z', {
      modifiers: ['platform'],
      allowedInteractionModes: ['canvas'],
    }),
  ],
  cycleViewMode: [new KeyboardShortcut('r')],
  yawLeft: [
    new KeyboardShortcut('ArrowLeft', {
      allowRepeat: true,
    }),
  ],
  yawLeftSlow: [
    new KeyboardShortcut('ArrowLeft', {
      modifiers: ['shiftKey'],
      allowRepeat: true,
    }),
  ],
  yawRight: [
    new KeyboardShortcut('ArrowRight', {
      allowRepeat: true,
    }),
  ],
  yawRightSlow: [
    new KeyboardShortcut('ArrowRight', {
      modifiers: ['shiftKey'],
      allowRepeat: true,
    }),
  ],
};

export function isKeyboardShortcutName(
  shortcutName: string,
): shortcutName is KeyboardShortcutName {
  return keyboardShortcuts.hasOwnProperty(shortcutName);
}
